/**
 * SEO Helper Utility
 * Provides page metadata for dynamic SEO implementation
 *
 * Usage: const pageMeta = getPageMeta('transactionHistory', language);
 * Returns: { title, description, url, image, keywords }
 */
// eslint-disable-next-line no-unused-vars
export const getPageMeta = (page, language = 'en') => {
    const baseUrl = 'https://app.xpenso.xyz';
    const baseImage = 'https://app.xpenso.xyz/logo512.png';

    const pages = {
        home: {
            en: {
                title: 'Xpenso - Smart Expense Tracker | Manage Your Money',
                description:
                    'The easiest way to track expenses, income, and daily hisab in Bangladesh. Take control of your finances with Xpenso.',
                url: `${baseUrl}/`,
                image: baseImage,
                keywords:
                    'expense tracker, money management app, daily hisab, budget planner Bangladesh',
            },
            bn: {
                title: 'Xpenso - স্মার্ট খরচ হিসাব | আপনার টাকার হিসাব রাখুন',
                description:
                    'সহজেই প্রতিদিনের খরচ ও আয়ের হিসাব রাখুন। আপনার ব্যক্তিগত অর্থ ব্যবস্থাপনার জন্য সেরা অ্যাপ এক্সপেনসো (Xpenso)।',
                url: `${baseUrl}/`,
                image: baseImage,
                keywords: 'খরচের হিসাব, হিসাব খাতা, টাকা জমানোর উপায়, মানি ম্যানেজমেন্ট',
            },
        },
        login: {
            en: {
                title: 'Login | Xpenso - Access Your Money Tracker',
                description:
                    'Log in to your Xpenso account to manage your daily expenses and view financial reports.',
                url: `${baseUrl}/login`,
                image: baseImage,
                keywords: 'xpenso login, expense tracker sign in',
            },
            bn: {
                title: 'লগইন করুন | Xpenso - আপনার ড্যাশবোর্ড দেখুন',
                description: 'আপনার এক্সপেনসো অ্যাকাউন্টে লগইন করুন এবং খরচের হিসাব পরিচালনা করুন।',
                url: `${baseUrl}/login`,
                image: baseImage,
                keywords: 'লগইন, এক্সপেনসো লগইন, একাউন্ট অ্যাক্সেস',
            },
        },
        register: {
            en: {
                title: 'Join Xpenso | Start Tracking Your Expenses for Free',
                description:
                    'Create a free account on Xpenso today. The best tool for personal finance and budget tracking in Bangladesh.',
                url: `${baseUrl}/register`,
                image: baseImage,
                keywords: 'create account, sign up xpenso, free expense tracker',
            },
            bn: {
                title: 'অ্যাকাউন্ট খুলুন | Xpenso - আজই শুরু করুন ফ্রি',
                description:
                    'এক্সপেনসোতে আজই ফ্রি অ্যাকাউন্ট খুলুন এবং স্মার্টলি আপনার খরচের হিসাব রাখুন।',
                url: `${baseUrl}/register`,
                image: baseImage,
                keywords: 'অ্যাকাউন্ট রেজিস্ট্রেশন, সাইন আপ, খরচ ট্র্যাকার',
            },
        },
        forgotPassword: {
            en: {
                title: 'Reset Password | Xpenso - Secure Account Recovery',
                description:
                    'Recover access to your Xpenso account. Enter your registered email to receive a secure password reset link.',
                url: `${baseUrl}/forgot-password`,
                image: baseImage,
                keywords: 'forgot password, password reset, account recovery, xpenso security',
                noindex: true, // Recommended: Keep utility pages off Google to boost main page authority
            },
            bn: {
                title: 'পাসওয়ার্ড রিসেট | Xpenso - অ্যাকাউন্ট পুনরুদ্ধার করুন',
                description:
                    'আপনার এক্সপেনসো অ্যাকাউন্টের পাসওয়ার্ড ভুলে গেছেন? আপনার ইমেইল দিয়ে সহজেই অ্যাকাউন্ট পুনরুদ্ধার করুন।',
                url: `${baseUrl}/forgot-password`,
                image: baseImage,
                keywords:
                    'পাসওয়ার্ড ভুলে গেছি, পাসওয়ার্ড রিসেট, অ্যাকাউন্ট রিকভারি, এক্সপেনসো পাসওয়ার্ড',
                noindex: true,
            },
        },
        resetPassword: {
            en: {
                title: 'Reset Password | Xpenso - Secure Your Account',
                description:
                    'Create a new, strong password for your Xpenso account to keep your financial data safe.',
                url: `${baseUrl}/reset-password`,
                image: baseImage,
                keywords: 'reset password, account recovery, xpenso security',
                noindex: true, // Keep search bots focused on your marketing pages
            },
            bn: {
                title: 'পাসওয়ার্ড রিসেট করুন | Xpenso - অ্যাকাউন্ট নিরাপদ রাখুন',
                description:
                    'আপনার এক্সপেনসো অ্যাকাউন্টের জন্য নতুন পাসওয়ার্ড সেট করুন এবং আপনার তথ্য সুরক্ষিত রাখুন।',
                url: `${baseUrl}/reset-password`,
                image: baseImage,
                keywords: 'পাসওয়ার্ড রিসেট, নতুন পাসওয়ার্ড, অ্যাকাউন্ট সিকিউরিটি',
                noindex: true,
            },
        },
        transactionHistory: {
            en: {
                title: 'Transaction History | Xpenso - All Your Money Movements',
                description:
                    'Easily view, search, and filter your financial history. Track every taka spent or earned in one secure place.',
                url: `${baseUrl}/transaction-history`,
                image: baseImage,
                keywords:
                    'transaction history, expense list, search transactions, money tracker Bangladesh',
                noindex: true, // Crucial: Keeps private data off Google results
            },
            bn: {
                title: 'লেনদেনের ইতিহাস | Xpenso - সব হিসাব এক জায়গায়',
                description:
                    'আপনার প্রতিদিনের আয়-ব্যয়ের বিস্তারিত ইতিহাস দেখুন। সহজে লেনদেন ফিল্টার করুন এবং হিসাব গুছিয়ে রাখুন।',
                url: `${baseUrl}/transaction-history`,
                image: baseImage,
                keywords: 'লেনদেনের ইতিহাস, খরচের তালিকা, হিসাব খাতা, দৈনিক লেনদেন',
                noindex: true,
            },
        },
        accounts: {
            en: {
                title: 'My Accounts & Wallets | Xpenso - Manage All Balances',
                description:
                    'Track your bank accounts, MFS (bKash/Nagad), and cash wallets in one secure dashboard. Stay updated on your total net worth.',
                url: `${baseUrl}/accounts`,
                image: baseImage,
                keywords:
                    'manage bank accounts, digital wallet tracker, bKash history, Nagad account manager, net worth tracker',
                noindex: true,
            },
            bn: {
                title: 'আমার অ্যাকাউন্ট ও ওয়ালেট | Xpenso - সব ব্যালেন্স একসাথে',
                description:
                    'আপনার ব্যাংক, বিকাশ বা নগদ এবং ক্যাশ ওয়ালেটের হিসাব এক জায়গায় রাখুন। আপনার মোট টাকার পরিমাণ জানুন সহজেই।',
                url: `${baseUrl}/accounts`,
                image: baseImage,
                keywords: 'ব্যাংক একাউন্ট, বিকাশ হিসাব, নগদ ওয়ালেট, টাকা জমানোর অ্যাপ',
                noindex: true,
            },
        },
        accountDetails: {
            en: {
                title: 'Account Insights | Xpenso - Detailed Statement',
                description:
                    'Deep dive into this account’s performance. View balance trends, categorized spending, and full transaction history.',
                url: `${baseUrl}/account`, // Ensure this dynamically appends the ID in your logic if possible
                image: baseImage,
                keywords: 'account statement, spending analysis, balance trends, financial details',
                noindex: true,
            },
            bn: {
                title: 'অ্যাকাউন্টের বিস্তারিত | Xpenso - লেনদেনের স্টেটমেন্ট',
                description:
                    'এই অ্যাকাউন্টের ব্যালেন্স ট্রেন্ড এবং খরচের বিস্তারিত রিপোর্ট দেখুন। আপনার লেনদেনের পূর্ণাঙ্গ স্টেটমেন্ট এখানে পাবেন।',
                url: `${baseUrl}/account`,
                image: baseImage,
                keywords: 'অ্যাকাউন্ট স্টেটমেন্ট, খরচের বিশ্লেষণ, ব্যালেন্স রিপোর্ট',
                noindex: true,
            },
        },
        reports: {
            en: {
                title: 'Financial Reports | Xpenso - Spending Analytics & Charts',
                description:
                    'Get a clear view of your money with visual charts. Analyze your spending habits and income trends to save more.',
                url: `${baseUrl}/reports`,
                image: baseImage,
                keywords:
                    'expense analytics, financial charts, spending trends, money saving reports',
                noindex: true,
            },
            bn: {
                title: 'আর্থিক রিপোর্ট | Xpenso - খরচের গ্রাফ ও বিশ্লেষণ',
                description:
                    'গ্রাফ এবং চার্টের মাধ্যমে আপনার আয়-ব্যয়ের বিস্তারিত রিপোর্ট দেখুন। আপনার টাকা কোথায় খরচ হচ্ছে তা জানুন খুব সহজেই।',
                url: `${baseUrl}/reports`,
                image: baseImage,
                keywords: 'খরচের রিপোর্ট, আয়-ব্যয় বিশ্লেষণ, গ্রাফ চার্ট, হিসাব নিকাশ',
                noindex: true,
            },
        },
        reportBalance: {
            en: {
                title: 'Balance Trends | Xpenso - Track Your Savings Growth',
                description:
                    'Monitor how your total balance changes over time. See your savings grow and track account history in one secure place.',
                url: `${baseUrl}/report/balance`,
                image: baseImage,
                keywords: 'balance trends, savings tracker, account history, financial stability',
                noindex: true,
            },
            bn: {
                title: 'ব্যালেন্স রিপোর্ট | Xpenso - জমানো টাকার হিসাব',
                description:
                    'সময়ের সাথে আপনার জমানো টাকার পরিমাণ কীভাবে বাড়ছে তা দেখুন। আপনার সব অ্যাকাউন্টের ব্যালেন্স হিস্ট্রি ট্র্যাক করুন সহজে।',
                url: `${baseUrl}/report/balance`,
                image: baseImage,
                keywords:
                    'ব্যালেন্স রিপোর্ট, সেভিংস রিপোর্ট, জমানো টাকার হিসাব, ব্যালেন্স হিস্ট্রি',
                noindex: true,
            },
        },
        reportIncome: {
            en: {
                title: 'Income Insights | Xpenso - Analyze Your Earnings',
                description:
                    'See exactly where your money comes from. Track salary, bonuses, and side-income sources with detailed charts.',
                url: `${baseUrl}/report/income`,
                image: baseImage,
                keywords: 'income analysis, earnings tracker, salary reports, income sources',
                noindex: true,
            },
            bn: {
                title: 'আয়ের হিসাব | Xpenso - উপার্জনের বিস্তারিত রিপোর্ট',
                description:
                    'আপনার আয়ের উৎস এবং প্রবণতা বিশ্লেষণ করুন। আপনার বেতন, বোনাস বা অন্যান্য উপার্জনের হিসাব রাখুন এক জায়গায়।',
                url: `${baseUrl}/report/income`,
                image: baseImage,
                keywords: 'আয়ের হিসাব, ইনকাম রিপোর্ট, উপার্জনের তালিকা, ইনকাম সোর্স',
                noindex: true,
            },
        },
        cashFlowReport: {
            en: {
                title: 'Cash Flow Analysis | Xpenso - Track Money In & Out',
                description:
                    'Understand your money movement. See the balance between what you earn and what you spend every month.',
                url: `${baseUrl}/report/cash-flow`,
                image: baseImage,
                keywords:
                    'cash flow tracker, money movement, inflow outflow analysis, financial health',
                noindex: true,
            },
            bn: {
                title: 'ক্যাশ-ফ্লো রিপোর্ট | Xpenso - টাকা আসার ও যাওয়ার হিসাব',
                description:
                    'টাকা কোথায় আসছে আর কোথায় যাচ্ছে তার নিখুঁত হিসাব দেখুন। ইনকাম এবং খরচের ভারসাম্য বজায় রাখুন খুব সহজেই।',
                url: `${baseUrl}/report/cash-flow`,
                image: baseImage,
                keywords: 'ক্যাশ ফ্লো, টাকা আসার হিসাব, ইনকাম ভার্সেস খরচ, অর্থ চলাচল',
                noindex: true,
            },
        },
        profile: {
            en: {
                title: 'My Profile | Xpenso - Personalize Your Finance Tracker',
                description:
                    'Customize your Xpenso account. Set your currency, manage security settings, and personalize your daily expense tracking experience.',
                url: `${baseUrl}/profile`,
                image: baseImage,
                keywords:
                    'Xpenso profile, expense tracker settings, personal finance manager, manage account BDT',
                noindex: true, // User-specific data - don't index
            },
            bn: {
                title: 'আমার প্রোফাইল | Xpenso - আপনার খরচের হিসাব গুছিয়ে নিন',
                description:
                    'আপনার এক্সপেনসো (Xpenso) অ্যাকাউন্ট সেটআপ করুন। নিজের প্রোফাইল, কারেন্সি এবং সিকিউরিটি সেটিংস পরিবর্তন করুন খুব সহজে।',
                url: `${baseUrl}/profile`,
                image: baseImage,
                keywords: 'খরচের হিসাব, প্রোফাইল সেটিংস, টাকা জমানোর অ্যাপ, ডেইলি হিসাব',
                noindex: true, // User-specific data - don't index
            },
        },
        myAccount: {
            en: {
                title: 'My Profile | Xpenso - Personal Settings',
                description:
                    'Update your personal details, profile picture, and account preferences in one place.',
                url: `${baseUrl}/my-account`,
                image: baseImage,
                keywords: 'user profile, account settings, personal details, xpenso account',
                noindex: true,
            },
            bn: {
                title: 'আমার প্রোফাইল | Xpenso - ব্যক্তিগত সেটিংস',
                description:
                    'আপনার ব্যক্তিগত তথ্য, প্রোফাইল ছবি এবং অ্যাপের পছন্দগুলো পরিবর্তন করুন।',
                url: `${baseUrl}/my-account`,
                image: baseImage,
                keywords: 'প্রোফাইল সেটিংস, ব্যক্তিগত তথ্য, ইউজার প্রোফাইল',
                noindex: true,
            },
        },
        changePassword: {
            en: {
                title: 'Change Password | Xpenso Security',
                description:
                    'Keep your financial data safe by regularly updating your account password.',
                url: `${baseUrl}/change-password`,
                image: baseImage,
                keywords: 'change password, account security, update password',
                noindex: true,
            },
            bn: {
                title: 'পাসওয়ার্ড পরিবর্তন | Xpenso সিকিউরিটি',
                description: 'অ্যাকাউন্টের নিরাপত্তা নিশ্চিত করতে আপনার পাসওয়ার্ড পরিবর্তন করুন।',
                url: `${baseUrl}/change-password`,
                image: baseImage,
                keywords: 'পাসওয়ার্ড পরিবর্তন, একাউন্ট নিরাপত্তা, সিকিউরিটি আপডেট',
                noindex: true,
            },
        },
        settings: {
            en: {
                title: 'App Settings | Xpenso - Customize Your Experience',
                description:
                    'Configure your currency (BDT), notification alerts, and language preferences.',
                url: `${baseUrl}/settings`,
                image: baseImage,
                keywords: 'app settings, customize xpenso, currency settings, bdt tracker',
                noindex: true,
            },
            bn: {
                title: 'সেটিংস | Xpenso - অ্যাপ কাস্টমাইজ করুন',
                description: 'আপনার পছন্দের কারেন্সি, নোটিফিকেশন এবং ভাষা সেটিংস ঠিক করে নিন।',
                url: `${baseUrl}/settings`,
                image: baseImage,
                keywords: 'সেটিংস, কারেন্সি সেটিংস, অ্যাপ কনফিগারেশন',
                noindex: true,
            },
        },
        cards: {
            en: {
                title: 'My Cards | Xpenso - Manage Debit/Credit Cards',
                description:
                    'Add and manage your payment cards to track card-specific spending and bills.',
                url: `${baseUrl}/cards`,
                image: baseImage,
                keywords: 'manage credit cards, debit card tracker, payment methods',
                noindex: true,
            },
            bn: {
                title: 'আমার কার্ড | Xpenso - ক্রেডিট ও ডেবিট কার্ড',
                description: 'আপনার সব ক্রেডিট ও ডেবিট কার্ডের হিসাব এক জায়গায় ম্যানেজ করুন।',
                url: `${baseUrl}/cards`,
                image: baseImage,
                keywords: 'ক্রেডিট কার্ড, ডেবিট কার্ড, কার্ড ম্যানেজমেন্ট, পেমেন্ট কার্ড',
                noindex: true,
            },
        },
        helpCenter: {
            en: {
                title: 'Help Center | Xpenso - FAQ & Tutorials',
                description:
                    'Need help tracking expenses? Find answers to FAQs and learn how to use Xpenso features.',
                url: `${baseUrl}/help-center`,
                image: baseImage,
                keywords: 'xpenso help, faq, tutorials, support',
                // This page is public, so NO noindex here!
            },
            bn: {
                title: 'সাহায্য কেন্দ্র | Xpenso - সাধারণ জিজ্ঞাসা ও সাপোর্ট',
                description:
                    'এক্সপেনসো ব্যবহার নিয়ে কোনো সমস্যা? আমাদের টিউটোরিয়াল এবং প্রশ্নোত্তরগুলো দেখুন।',
                url: `${baseUrl}/help-center`,
                image: baseImage,
                keywords: 'হেল্প সেন্টার, সাধারণ প্রশ্ন, সাপোর্ট, টিউটোরিয়াল',
            },
        },
        categoryExpense: {
            en: {
                title: 'Category Breakdown | Xpenso - Where Do You Spend?',
                description:
                    'Analyze spending by category. See if you are spending too much on food, shopping, or rent.',
                url: `${baseUrl}/category-expense`,
                image: baseImage,
                keywords: 'spending categories, expense breakdown, category analysis',
                noindex: true,
            },
            bn: {
                title: 'খরচের খাত | Xpenso - ক্যাটাগরি অনুযায়ী হিসাব',
                description:
                    'আপনার খরচের খাতগুলো বিশ্লেষণ করুন। বাজার, খাবার নাকি শপিং—কোথায় বেশি খরচ হচ্ছে দেখুন।',
                url: `${baseUrl}/category-expense`,
                image: baseImage,
                keywords: 'খরচের খাত, ক্যাটাগরি বিশ্লেষণ, বাজার খরচ, শপিং হিসাব',
                noindex: true,
            },
        },
        statistics: {
            en: {
                title: 'Financial Statistics | Xpenso - Visual Insights',
                description:
                    'A birds-eye view of your financial health with interactive charts and monthly trends.',
                url: `${baseUrl}/statistics`,
                image: baseImage,
                keywords: 'financial statistics, money trends, expense charts',
                noindex: true,
            },
            bn: {
                title: 'পরিসংখ্যান | Xpenso - গ্রাফ ও চার্টে খরচের হিসাব',
                description:
                    'আপনার আয় ও খরচের বিস্তারিত পরিসংখ্যান ও মাসিক ট্রেন্ড দেখুন গ্রাফের মাধ্যমে।',
                url: `${baseUrl}/statistics`,
                image: baseImage,
                keywords: 'আর্থিক পরিসংখ্যান, খরচের গ্রাফ, মাসিক রিপোর্ট',
                noindex: true,
            },
        },
        passwordUpdate: {
            en: {
                title: 'Update Password | Xpenso - Secure Your Account',
                description:
                    'Change your current password to a new, secure one. Protect your financial data with strong account security.',
                url: `${baseUrl}/password-update`,
                image: baseImage,
                keywords: 'update password, change password, account security, xpenso login',
                noindex: true,
            },
            bn: {
                title: 'পাসওয়ার্ড আপডেট | Xpenso - অ্যাকাউন্ট সুরক্ষিত রাখুন',
                description:
                    'আপনার পাসওয়ার্ড পরিবর্তন করে অ্যাকাউন্ট আরও নিরাপদ করুন। একটি শক্তিশালী পাসওয়ার্ড সেট করুন সহজেই।',
                url: `${baseUrl}/password-update`,
                image: baseImage,
                keywords: 'পাসওয়ার্ড আপডেট, পাসওয়ার্ড পরিবর্তন, সিকিউরিটি, একাউন্ট সুরক্ষা',
                noindex: true,
            },
        },
        notification: {
            en: {
                title: 'Notifications | Xpenso - Alerts & Updates',
                description:
                    'Stay updated with real-time alerts about your transactions, bill reminders, and account activity.',
                url: `${baseUrl}/notification`,
                image: baseImage,
                keywords: 'notifications, account alerts, bill reminders, xpenso updates',
                noindex: true,
            },
            bn: {
                title: 'বিজ্ঞপ্তি | Xpenso - সব আপডেট এক জায়গায়',
                description:
                    'আপনার লেনদেন, বিল রিমাইন্ডার এবং অ্যাকাউন্ট সম্পর্কিত সব নোটিফিকেশন দেখুন।',
                url: `${baseUrl}/notification`,
                image: baseImage,
                keywords: 'বিজ্ঞপ্তি, নোটিফিকেশন, ট্রানজ্যাকশন এলার্ট, আপডেট',
                noindex: true,
            },
        },
        newUserSettings: {
            en: {
                title: 'Onboarding | Xpenso - Quick App Setup',
                description:
                    'Welcome to Xpenso! Set your preferred currency, language, and initial budget to get started.',
                url: `${baseUrl}/new-user`,
                image: baseImage,
                keywords: 'app setup, onboarding, setup wizard, get started xpenso',
                noindex: true,
            },
            bn: {
                title: 'অ্যাপ সেটআপ | Xpenso - যাত্রা শুরু করুন',
                description:
                    'এক্সপেনসো-তে আপনাকে স্বাগতম! আপনার পছন্দের কারেন্সি, ভাষা এবং প্রাথমিক বাজেট সেটআপ করে নিন।',
                url: `${baseUrl}/new-user`,
                image: baseImage,
                keywords: 'অনবোর্ডিং, অ্যাপ সেটআপ, নতুন ইউজার, কনফিগারেশন',
                noindex: true,
            },
        },
    };

    const pageMeta = pages[page] || pages.home;

    // Handle language-specific metadata
    if (pageMeta[language]) {
        return pageMeta[language];
    }

    // Unknown language: every page defines English metadata
    return pageMeta.en;
};
